import type { Category } from "@arc/contracts";

export interface PartitionedCategories {
  /** Attached categories matching the query (rendered under "On this facility"). */
  attached: Category[];
  /** Unattached categories matching the query (rendered under "Available in the library"). */
  library: Category[];
  /** UNFILTERED attached total — the section badge (stays stable while a search hides rows). */
  attachedTotal: number;
  /** UNFILTERED library total — the section badge. */
  libraryTotal: number;
  /** The trimmed query equals an existing category name (any case) anywhere in the realm → suppress "create". */
  hasExactMatch: boolean;
}

/**
 * Split the realm's categories into "on this facility" (attached) vs "available in the library" (unattached), then
 * filter each by the search query. Pure so it's unit-testable without rendering (mirrors `facility-tree.ts`).
 *
 * Badges use the UNFILTERED totals; the returned lists are the filtered rows. `hasExactMatch` dedups the "create"
 * affordance against ALL categories (creating a name that already exists would 409 on the unique-name constraint).
 */
export function partitionCategories(
  all: readonly Category[],
  attachedIds: ReadonlySet<number>,
  rawQuery: string,
): PartitionedCategories {
  const q = rawQuery.trim().toLowerCase();
  const attachedAll = all.filter((c) => attachedIds.has(c.id));
  const libraryAll = all.filter((c) => !attachedIds.has(c.id));
  const match = (c: Category) => q === "" || c.name.toLowerCase().includes(q);
  return {
    attached: attachedAll.filter(match),
    library: libraryAll.filter(match),
    attachedTotal: attachedAll.length,
    libraryTotal: libraryAll.length,
    hasExactMatch: q !== "" && all.some((c) => c.name.trim().toLowerCase() === q),
  };
}
